import { PersonLastSearch } from "@/pages/Private/redux/searchDefaults/searchDefaults.schema";

import { CreateCompanyEnrich } from "../schema/companyEnrich";

type FormValues = Partial<CreateCompanyEnrich>;

/**
 * Search defaults in the Lead Database / Lookalike source form (PREC-3666).
 *
 * The form remembers the fields of `PersonLastSearch` — the person filters, the company location
 * with its states and cities, industries, headcount, keywords, regions and the exclusions — and
 * nothing else: the source name, the mode, the lookalike seeds, tenure, education and funding
 * belong to one source, not to the user's way of searching.
 */

/**
 * The form with a search applied: every remembered field is taken from `search`, and a field the
 * search does not hold is cleared, so the company preset (which holds only its own fields) also
 * clears the states, cities, keywords and exclusions a previous search left. Everything else stays.
 */
export const applySearchToFormValues = (
	search: PersonLastSearch,
	current: FormValues
): FormValues => {
	const companyFilter = search.companyFilterSearch;

	return {
		...current,
		positionQuery: [...(search.positionQuery ?? [])],
		seniority: [...(search.seniority ?? [])],
		department: [...(search.department ?? [])],
		countries: [...(search.countries ?? [])],
		companyCountries: [...(search.companyCountries ?? [])],
		companyStates: [...(search.companyStates ?? [])],
		companyCities: [...(search.companyCities ?? [])],
		excludePositionQuery: [...(search.excludePositionQuery ?? [])],
		excludeDepartment: [...(search.excludeDepartment ?? [])],
		excludeCountries: [...(search.excludeCountries ?? [])],
		excludeDomains: [...(search.excludeDomains ?? [])],
		companyFilterSearch: {
			...current.companyFilterSearch,
			employees: [...(companyFilter?.employees ?? [])],
			industries: (companyFilter?.industries ?? []).map((industry) => ({
				name: industry.name,
				naicsCodes: [...industry.naicsCodes],
			})),
			keywords: [...(companyFilter?.keywords ?? [])],
			regions: [...(companyFilter?.regions ?? [])],
			exclude: companyFilter?.exclude ? { ...companyFilter.exclude } : undefined,
		},
	};
};

/**
 * What the form sends as the user's last search: every remembered field, an empty one as `[]` so
 * that clearing a filter clears it in the stored search too.
 */
export const lastSearchFromFormValues = (values: FormValues): PersonLastSearch => ({
	positionQuery: values.positionQuery ?? [],
	seniority: values.seniority ?? [],
	department: values.department ?? [],
	countries: values.countries ?? [],
	companyCountries: values.companyCountries ?? [],
	companyStates: values.companyStates ?? [],
	companyCities: values.companyCities ?? [],
	excludePositionQuery: values.excludePositionQuery ?? [],
	excludeDepartment: values.excludeDepartment ?? [],
	excludeCountries: values.excludeCountries ?? [],
	excludeDomains: values.excludeDomains ?? [],
	companyFilterSearch: {
		employees: values.companyFilterSearch?.employees ?? [],
		industries: values.companyFilterSearch?.industries ?? [],
		keywords: values.companyFilterSearch?.keywords ?? [],
		regions: values.companyFilterSearch?.regions ?? [],
		exclude: values.companyFilterSearch?.exclude ?? {},
	},
});

/**
 * Whether two country lists name the same countries, in any order. Two empty lists are the same:
 * neither narrows the search.
 */
export const hasSameCountries = (
	left: string[] | null | undefined,
	right: string[] | null | undefined
): boolean => {
	const leftCodes = new Set(left ?? []);
	const rightCodes = new Set(right ?? []);

	return leftCodes.size === rightCodes.size && [...leftCodes].every((code) => rightCodes.has(code));
};
